import React, { useEffect, useState } from 'react'
import './styles.scss'
import { DateSlider } from 'components/DateSlider'
import { Chart } from 'components/Chart'
import { getExtent } from 'components/Charts_Assets'

const cantoncodes =
  'CH,AG,AI,AR,BE,BL,BS,FR,GE,GL,GR,JU,LU,NE,NW,OW,SG,SH,SO,SZ,TG,TI,UR,VD,VS,ZG,ZH'.split(
    ','
  )

const waves = [
  { start: '2020-02-01', end: '2020-05-31' },
  { start: '2020-06-01', end: '2021-02-28' },
  { start: '2021-03-01', end: '2021-06-30' },
  { start: '2021-07-01', end: '2021-10-15' },
  { start: '2021-10-16', end: new Date().toISOString().split('T')[0] },
]

export const TimeCompare = ({
  isMobile = false,
  drawData,
  preselectedCanton,
}: {
  isMobile: boolean
  drawData: {
    [key: string]: { [key: string]: any[] }
  }
  preselectedCanton?: string
}) => {
  const [selectedRanges, setSelectedRanges] = useState<Date[][] | undefined>()

  const [selectedCanton, setSelectedCanton] = useState(
    preselectedCanton ?? 'CH'
  )
  const [yDomains, setYDomains] = useState<{ [key: string]: any[] }>()

  useEffect(() => {
    if (!drawData) return
    handleCantonSelect(selectedCanton)
    handleSetPeak(1)
  }, [drawData])

  const handleCantonSelect = (canton: string) => {
    if (!drawData) return
    const cantondata = drawData[canton]
    setSelectedCanton(canton)
    setYDomains({
      cases: getExtent(cantondata.cases?.map((m) => m.v)),
      deaths: getExtent(cantondata.deaths?.map((m) => m.v)),
      hosp: getExtent(cantondata.hosp?.map((m) => m.v)),
      intense: getExtent(cantondata.intense?.map((m) => m.v)),
      ratio: getExtent(cantondata.hospRatio?.map((m) => m.v)),
    })
  }
  const handleSetPeak = (which: number) => {
    if (!drawData) return
    const dd = drawData[selectedCanton].cases
    const last = new Date(dd[dd.length - 1].d)
    const wave = waves[which]
    const waveData = dd.filter(
      (m) =>
        new Date(m.d) >= new Date(wave.start) &&
        new Date(m.d) <= new Date(wave.end)
    )
    const wavemax = Math.max(...waveData.map((m) => m.v))
    const peak = waveData.find((f) => f.v === wavemax)

    setSelectedRanges([
      [new Date(peak.d), last],
      [new Date(peak.d), last],
      [new Date(peak.d), last],
      [new Date(peak.d), last],
      [new Date(peak.d), last],
    ])
  }

  const handleRangeSelect = (range: Date[], id: number) => {
    if (id === 111) {
      setSelectedRanges([range, range, range, range, range])
    } else {
      let current = JSON.parse(JSON.stringify(selectedRanges))
      current[id] = [new Date(range[0]), new Date(range[1])]

      setSelectedRanges([
        [new Date(current[0][0]), new Date(current[0][1])],
        [new Date(current[1][0]), new Date(current[1][1])],
        [new Date(current[2][0]), new Date(current[2][1])],
        [new Date(current[3][0]), new Date(current[3][1])],
        [new Date(current[4][0]), new Date(current[4][1])],
      ])
    }
  }
  const startDate = isMobile ? new Date('2020-08-01') : new Date('2020-05-01')
  return (
    <>
      <div className='TimeCompare'>
        <div className='ui text container'>
          <div className='selectwrapper'>
            <CantonSelect
              entries={cantoncodes}
              handleSelect={handleCantonSelect}
              current={selectedCanton}
            />
            <PeakSelect
              entries={[
                ['2. Welle', 1],
                ['3. Welle', 2],
                ['4. Welle', 3],
              ]}
              handleSelect={handleSetPeak}
            />
          </div>
        </div>
        <div className='ui container parameters'>
          {drawData && selectedRanges && yDomains && (
            <>
              <div className='labelrow'>
                <div className='label'>Spanne für alle Parameter festlegen</div>
                <DateSlider
                  selectedRange={selectedRanges[0]}
                  selectRange={handleRangeSelect}
                  id={111}
                  range={[
                    startDate,
                    new Date(
                      drawData[selectedCanton].cases[
                        drawData[selectedCanton].cases.length - 1
                      ].d
                    ),
                  ]}
                />
              </div>

              <Chart
                data={drawData[selectedCanton].deaths}
                selectedRange={selectedRanges[4]}
                selectRange={handleRangeSelect}
                id={4}
                yDomain={yDomains.deaths}
                classname='death'
                chartLabel='Todesfälle'
                chartLabelSub='7-Tages-Schnitt der Covid-Todesfälle, Quelle: BAG nach Meldedatum'
                isMobile={isMobile}
              />
              {drawData[selectedCanton].intense && (
                <Chart
                  data={drawData[selectedCanton].intense}
                  selectedRange={selectedRanges[3]}
                  selectRange={handleRangeSelect}
                  id={3}
                  yDomain={yDomains.intense}
                  classname='intense'
                  chartLabel='Intensivstation'
                  chartLabelSub='Tageswerte der mit Covid-Patienten belegten Intensivbetten, Quelle: Kantone'
                  isMobile={isMobile}
                />
              )}
              {drawData[selectedCanton].hosp && (
                <Chart
                  data={drawData[selectedCanton].hosp}
                  selectedRange={selectedRanges[2]}
                  selectRange={handleRangeSelect}
                  id={2}
                  yDomain={yDomains.hosp}
                  classname='hosp'
                  chartLabel='Spitaleintritte'
                  chartLabelSub='7-Tages-Schnitt der Spitaleintritte, Quelle: BAG nach Meldedatum'
                  isMobile={isMobile}
                />
              )}
              {drawData[selectedCanton].cases && (
                <Chart
                  data={drawData[selectedCanton].cases}
                  selectedRange={selectedRanges[1]}
                  id={1}
                  selectRange={handleRangeSelect}
                  yDomain={yDomains.cases}
                  classname='cases'
                  chartLabel='Bestätigte Fälle'
                  chartLabelSub='7-Tages-Schnitt der Neuinfektionen, Quelle: BAG nach Meldedatum'
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

const PeakSelect = ({
  entries,
  handleSelect,
}: {
  entries: [string, number][]
  handleSelect: (which: number) => void
}) => (
  <div className='peakselect'>
    <div className='title'>Höchstwerte der Fälle auswählen</div>
    {entries.map((m) => (
      <button
        key={m[1]}
        className='peakbutton '
        onClick={() => handleSelect(m[1])}
      >
        {m[0]}
      </button>
    ))}
  </div>
)

const CantonSelect = ({
  entries,
  handleSelect,
  current,
}: {
  entries: string[]
  handleSelect: (canton: string) => void
  current: string
}) => {
  return (
    <div className='cantonsselect'>
      <div className='title'>Kanton auswählen</div>
      {entries.map((m) => (
        <button
          key={m}
          className={
            'cantonbutton ' + (current === m ? 'selected' : ' ') + ' ' + m
          }
          onClick={() => handleSelect(m)}
        >
          {m === 'CH' ? 'SCHWEIZ' : m}
        </button>
      ))}
    </div>
  )
}
